import React, { useState, useEffect } from 'react';
import {
  MagnifyingGlassIcon,
  AdjustmentsHorizontalIcon,
  XMarkIcon,
  ChevronDownIcon,
  StarIcon as StarIconSolid
} from '@heroicons/react/24/solid';
import { StarIcon } from '@heroicons/react/24/outline';
import axios from 'axios';

const AdvancedSearch = ({ filters, onFiltersChange, onSearch, categories = [] }) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    brands: [],
    tags: [],
    priceRange: { min: 0, max: 1000 }
  });
  const [localFilters, setLocalFilters] = useState(filters);

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const fetchFilterOptions = async () => {
    try {
      const response = await axios.get('/api/products/filters');
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...localFilters, [key]: value };
    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleSearch = () => {
    onSearch(localFilters);
  };

  const clearFilters = () => {
    const clearedFilters = {
      category: '',
      minPrice: '',
      maxPrice: '',
      sort: 'created_at',
      order: 'desc',
      search: '',
      brand: '',
      minRating: '',
      inStock: '',
      tags: ''
    };
    setLocalFilters(clearedFilters);
    onFiltersChange(clearedFilters);
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <button
        key={index}
        type="button"
        onClick={() => handleFilterChange('minRating', index + 1)}
        className={`h-4 w-4 ${
          index < rating ? 'text-yellow-400' : 'text-gray-300'
        } hover:text-yellow-400 transition-colors`}
      >
        {index < rating ? (
          <StarIconSolid className="h-4 w-4" />
        ) : (
          <StarIcon className="h-4 w-4" />
        )}
      </button>
    ));
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
      {/* Basic Search */}
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-stretch sm:items-center">
        <div className="flex-1 relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="חפש מוצרים, מותגים, קוד מוצר..."
            value={localFilters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            className="w-full pl-10 pr-4 py-3 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-base sm:text-sm"
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          />
        </div>
        
        <div className="flex gap-2 sm:gap-3">
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className={`flex items-center justify-center space-x-2 px-3 sm:px-4 py-3 sm:py-2 rounded-lg border transition-colors flex-1 sm:flex-none ${
              showAdvanced 
                ? 'bg-primary-50 border-primary-200 text-primary-700' 
                : 'bg-gray-50 border-gray-300 text-gray-700 hover:bg-gray-100'
            }`}
          >
            <AdjustmentsHorizontalIcon className="h-5 w-5" />
            <span className="hidden sm:inline">פילטרים מתקדמים</span>
            <span className="sm:hidden">פילטרים</span>
            <ChevronDownIcon 
              className={`h-4 w-4 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} 
            />
          </button>

          <button
            onClick={handleSearch}
            className="bg-primary-600 text-white px-4 sm:px-6 py-3 sm:py-2 rounded-lg hover:bg-primary-700 transition-colors flex items-center justify-center space-x-2 flex-1 sm:flex-none"
          >
            <MagnifyingGlassIcon className="h-5 w-5" />
            <span className="hidden sm:inline">חפש</span>
          </button>
        </div>
      </div>

      {/* Advanced Filters */}
      {showAdvanced && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            
            {/* Category Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                קטגוריה
              </label>
              <select
                value={localFilters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">כל הקטגוריות</option>
                {Array.isArray(categories) && categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Brand Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                מותג
              </label>
              <select
                value={localFilters.brand}
                onChange={(e) => handleFilterChange('brand', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">כל המותגים</option>
                {filterOptions.brands.map((brand) => (
                  <option key={brand} value={brand}>
                    {brand}
                  </option>
                ))}
              </select>
            </div>

            {/* Price Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                טווח מחירים (₪)
              </label>
              <div className="flex space-x-2">
                <input
                  type="number"
                  placeholder="מ-"
                  value={localFilters.minPrice}
                  onChange={(e) => handleFilterChange('minPrice', e.target.value)}
                  className="w-1/2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
                <input
                  type="number"
                  placeholder="עד"
                  value={localFilters.maxPrice}
                  onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
                  className="w-1/2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            {/* Rating Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                דירוג מינימלי
              </label>
              <div className="flex items-center space-x-1">
                {renderStars(localFilters.minRating || 0)}
                <span className="text-sm text-gray-500 mr-2">
                  {localFilters.minRating ? `${localFilters.minRating}+` : 'הכל'}
                </span>
              </div>
            </div>

            {/* Stock Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                זמינות
              </label>
              <select
                value={localFilters.inStock}
                onChange={(e) => handleFilterChange('inStock', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">הכל</option>
                <option value="true">במלאי</option>
                <option value="false">אזל מהמלאי</option>
              </select>
            </div>

            {/* Sort Options */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                מיון לפי
              </label>
              <select
                value={`${localFilters.sort}-${localFilters.order}`}
                onChange={(e) => {
                  const [sort, order] = e.target.value.split('-');
                  handleFilterChange('sort', sort);
                  handleFilterChange('order', order);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="created_at-desc">החדשים ביותר</option>
                <option value="created_at-asc">הישנים ביותר</option>
                <option value="name-asc">שם (א-ת)</option>
                <option value="name-desc">שם (ת-א)</option>
                <option value="price_b2c-asc">מחיר (נמוך לגבוה)</option>
                <option value="price_b2c-desc">מחיר (גבוה לנמוך)</option>
                <option value="rating_average-desc">דירוג גבוה</option>
                <option value="sales_count-desc">הנמכרים ביותר</option>
                <option value="view_count-desc">הנצפים ביותר</option>
              </select>
            </div>

            {/* Tags Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                תגיות
              </label>
              <select
                value={localFilters.tags}
                onChange={(e) => handleFilterChange('tags', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">כל התגיות</option>
                {filterOptions.tags.map((tag) => (
                  <option key={tag} value={tag}>
                    {tag}
                  </option>
                ))}
              </select>
            </div>

            {/* Clear Filters Button */}
            <div className="flex items-end">
              <button
                onClick={clearFilters}
                className="w-full flex items-center justify-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <XMarkIcon className="h-4 w-4" />
                <span>נקה פילטרים</span>
              </button>
            </div>
          </div>

          {/* Active Filters Display */}
          <div className="mt-4 flex flex-wrap gap-2">
            {Object.entries(localFilters).map(([key, value]) => {
              if (!value || key === 'search' || key === 'sort' || key === 'order') return null;
              
              let displayValue = value;
              if (key === 'category') {
                const category = categories.find(c => c.id === value);
                displayValue = category?.name || value;
              } else if (key === 'minRating') {
                displayValue = `${value}+ כוכבים`;
              } else if (key === 'inStock') {
                displayValue = value === 'true' ? 'במלאי' : 'אזל מהמלאי';
              } else if (key === 'minPrice') {
                displayValue = `מ-${value}₪`;
              } else if (key === 'maxPrice') {
                displayValue = `עד ${value}₪`;
              }

              return (
                <span
                  key={key}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary-100 text-primary-800"
                >
                  {displayValue}
                  <button
                    onClick={() => handleFilterChange(key, '')}
                    className="mr-2 hover:text-primary-600"
                  >
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedSearch;
